import React from 'react';
import { Colors } from 'works/legacy/lib/style-helper';
import { Col, Row } from 'antd';
import { formatPhone } from 'works/legacy/services/phone-service';
import { MembershipByEmail } from 'works/graphql/works-membership-by-email';
import { ContentContainer } from '@common/components';

function buildPhone(data?: PreviousData | NewData) {
  if (!data) return;
  return formatPhone(data.phone || 'N/A');
}

type Info = string | number | undefined;

function InfoPresenter({
  newInfo,
  previousInfo,
}: {
  newInfo: Info;
  previousInfo: Info;
}) {
  const hasValueChanged = newInfo !== previousInfo;
  const showPreviousInfo = !!previousInfo && hasValueChanged;

  return (
    <span>
      {showPreviousInfo && (
        <span className="members-review-previous-data">{previousInfo}</span>
      )}
      {newInfo}
      <style jsx>{`
        .members-review-previous-data {
          color: ${Colors.midnightLighter};
          text-decoration: line-through;
          margin-right: 12px;
        }
      `}</style>
    </span>
  );
}

function InfoRow({
  label,
  newInfo,
  previousInfo,
}: {
  label: string;
  newInfo: Info;
  previousInfo?: Info;
}) {
  return (
    <Row id={label} gutter={24}>
      <Col span={4}>{label}</Col>
      <Col span={20}>
        <InfoPresenter newInfo={newInfo} previousInfo={previousInfo} />
      </Col>
    </Row>
  );
}

type NewData = {
  firstName?: string;
  lastName?: string;
  status?: string;
  email?: string;
  employeeId?: string;
  phone?: string;
  role?: string;
  timeZone?: string;
  title?: string;
  organizationUnitsCount?: number;
  organizationBusinessCount?: number;
};

type PreviousData = MembershipByEmail;

function buildName(membership?: PreviousData | NewData) {
  if (!membership) return;
  return `${membership.firstName} ${membership.lastName}`;
}

export function MembersAddEditStepReview({
  isOrganization,
  newData,
  previousData,
}: {
  isOrganization: boolean;
  newData: NewData;
  previousData?: PreviousData;
}) {
  return (
    <>
      <div className="members-review sentry-mask">
        <ContentContainer
          title={
            <InfoPresenter
              newInfo={buildName(newData)}
              previousInfo={buildName(previousData)}
            />
          }
        >
          <div className="members-review-data-section">
            <InfoRow
              label="E-mail"
              newInfo={newData.email}
              previousInfo={previousData?.email}
            />
            <InfoRow
              label="Employee ID"
              newInfo={newData.employeeId}
              previousInfo={
                // If the already existing membership is a worker, we will create a new membership, so we will only display the new employeeId
                previousData?.role === 'worker'
                  ? newData.employeeId
                  : previousData?.employeeId
              }
            />
            <InfoRow
              label="Phone Number"
              newInfo={buildPhone(newData)}
              previousInfo={buildPhone(previousData)}
            />
            <InfoRow
              label="Role"
              newInfo={newData.role}
              previousInfo={
                // If the already existing membership is a worker, we will create a new membership, so we will only display the new role
                previousData?.role === 'worker'
                  ? newData.role
                  : previousData?.role
              }
            />
            {newData.title && (
              <InfoRow
                label="Title"
                newInfo={newData?.title}
                previousInfo={previousData?.title}
              />
            )}
            <InfoRow
              label="Timezone"
              newInfo={newData.timeZone}
              previousInfo={previousData?.timeZone}
            />
            {isOrganization && (
              <>
                <InfoRow
                  label="Facilities"
                  newInfo={newData.organizationBusinessCount}
                  previousInfo={previousData?.organizationBusinessesActiveCount}
                />
                <InfoRow
                  label="Units"
                  newInfo={newData.organizationUnitsCount}
                  previousInfo={previousData?.organizationUnitsActive?.length}
                />
              </>
            )}
          </div>
        </ContentContainer>
      </div>
      <style jsx>{`
        .members-review-data-section :global(.ant-row .ant-col:first-child) {
          color: ${Colors.midnightLighter};
          font-weight: 200;
          margin-bottom: 4px;
        }
      `}</style>
    </>
  );
}
